/* ============================================================================
   ebarza · 3D Library — page-scoped JavaScript
   ----------------------------------------------------------------------------
   Loaded only on templates/page.3d-library.liquid (with `defer`).

   Modules:
     · Sticky header behavior (is-scrolled class, search dropdown, smooth scroll)
     · App-widget hider (MutationObserver kills late-injected app embeds)
     · Sugar Model Viewer (hero) — tab switching, AR, QR modal, fail-safe
     · Custom Measure button + dim toggle grooming
     · Download flow with customer gating
   ============================================================================ */

(function () {
  'use strict';

  const cfg = window.ebarza3D || {};
  const $   = (id) => document.getElementById(id);

  /* ============================================================================
     1. STICKY HEADER + SEARCH DROPDOWN + SMOOTH SCROLL
     ============================================================================ */
  const header        = document.querySelector('.ebarza3d-header');
  const searchTrigger = document.querySelector('.ebarza3d-search-trigger');
  const searchPanel   = document.querySelector('.ebarza3d-search');
  const searchClose   = document.querySelector('.ebarza3d-search__close');
  const searchInput   = document.querySelector('.ebarza3d-search__input');

  // Scroll state
  if (header) {
    const onScroll = () => {
      if (window.scrollY > 6) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function openSearch() {
    if (!searchPanel) return;
    searchPanel.hidden = false;
    searchTrigger?.setAttribute('aria-expanded', 'true');
    setTimeout(() => searchInput?.focus(), 30);
  }
  function closeSearch() {
    if (!searchPanel) return;
    searchPanel.hidden = true;
    searchTrigger?.setAttribute('aria-expanded', 'false');
  }
  /* Idempotent wire-up — the header section's inline JS (and
     ebarza3d-additions.js) also wire this trigger. The first to bind
     stamps data-search-wired="1"; subsequent handlers skip so a single
     click doesn't toggle the panel twice (open → immediately closed). */
  if (searchTrigger && searchTrigger.dataset.searchWired !== '1') {
    searchTrigger.dataset.searchWired = '1';
    searchTrigger.addEventListener('click', () => {
      if (searchPanel?.hidden) openSearch(); else closeSearch();
    });
    searchClose?.addEventListener('click', closeSearch);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !searchPanel?.hidden) closeSearch();
    });
  }

  // Smooth scroll for anchor nav links (header + footer + trade CTA)
  document.querySelectorAll('a[data-scroll]').forEach(link => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href') || '';
      const hashIndex = href.indexOf('#');
      if (hashIndex < 0) return;
      const id = href.slice(hashIndex + 1);
      const target = document.getElementById(id);
      if (!target) return;
      // Only intercept same-page jumps
      if (href.startsWith('#') || href.startsWith('/pages/3d-library')) {
        e.preventDefault();
        const headerH = header?.offsetHeight || 72;
        const top = target.getBoundingClientRect().top + window.scrollY - headerH - 12;
        window.scrollTo({ top, behavior: 'smooth' });
        history.replaceState(null, '', '#' + id);
        closeSearch();
      }
    });
  });

  /* ============================================================================
     2. APP-WIDGET HIDER — kill late-injected 3rd-party UIs on this page only
     ============================================================================ */
  const APP_PATTERNS = [
    /whatsapp/i, /wa-chat/i, /wa-widget/i, /wa[-_]?button/i,
    /reviews?[-_]?tab/i, /reviews?[-_]?widget/i, /productreviews/i,
    /growave/i, /loox/i, /judge[-_]?me/i, /yotpo/i, /tipo/i,
    /miles/i, /smile[-_]?launcher/i, /gift[-_]?voucher/i,
    /popup[-_]?widget/i, /popup[-_]?window/i,
    /trustpilot/i, /zoko/i, /boom/i,
    /shopify-app-embed/i
  ];
  function looksLikeAppWidget(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.closest('.ebarza3d, .ebarza3d-header, .ebarza3d-footer, #ebarza3d-search')) return false;
    if (el.classList?.contains('ebarza3d-allowed')) return false;
    const id  = el.id || '';
    const cls = (typeof el.className === 'string' ? el.className : '') || '';
    return APP_PATTERNS.some(rx => rx.test(id) || rx.test(cls));
  }
  function hideEl(el) {
    el.style.setProperty('display', 'none', 'important');
    el.style.setProperty('visibility', 'hidden', 'important');
    el.style.setProperty('pointer-events', 'none', 'important');
  }
  function sweep(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('*').forEach(el => {
      if (looksLikeAppWidget(el)) hideEl(el);
    });
  }
  sweep(document.body);
  const widgetObserver = new MutationObserver(muts => {
    muts.forEach(m => {
      m.addedNodes.forEach(n => {
        if (n.nodeType !== 1) return;
        if (looksLikeAppWidget(n)) hideEl(n);
        if (n.querySelectorAll) sweep(n);
      });
    });
  });
  widgetObserver.observe(document.body, { childList: true, subtree: true });

  /* ============================================================================
     3. HERO SUGAR VIEWER — tab switching, AR, QR modal, fail-safe, measure
     ============================================================================ */
  const mv       = $('hero-mv');
  let   sugar    = $('hero-sugar');
  const stage    = $('stage');
  const skeleton = $('hero-skeleton');
  const bar      = $('hero-progress-bar');
  const title    = $('hero-title');
  const specs    = $('hero-specs');
  const tabs     = document.querySelectorAll('.info-tabs .tab');
  const arBtn    = $('ar-btn');
  const skelMsg  = $('hero-skel-msg');
  const skelSub  = $('hero-skel-sub');
  const retryBtn = $('hero-retry');

  if (!sugar || !stage) return;

  stage.addEventListener('wheel', (e) => {
    const rect = stage.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = x / rect.width;
    if (ratio < 0.35 || ratio > 0.65) e.stopImmediatePropagation();
  }, { capture: true, passive: true });

  let activeViewer = 'sugar';
  let sugarReady = false;
  let sugarFailTimer = null;
  let canActivateAR = false;

  function showSkeleton(msg, sub) {
    skeleton.classList.remove('fading');
    skeleton.hidden = false;
    bar.style.width = '0%';
    // innerHTML so the inline ;) wink span can render. msg is trusted —
    // only ever called from this file with hard-coded strings.
    skelMsg.innerHTML = msg || "wait it's rendering <span class=\"hero-render-msg__wink\" aria-hidden=\"true\">;)</span>";
    if (sub) { skelSub.textContent = sub; skelSub.classList.remove('hidden'); }
    else { skelSub.classList.add('hidden'); }
    retryBtn.classList.add('hidden');
  }
  function hideSkeleton() {
    skeleton.classList.add('fading');
    setTimeout(() => { skeleton.hidden = true; }, 600);
  }
  function showError(msg, sub) {
    skeleton.classList.remove('fading');
    skeleton.hidden = false;
    skelMsg.textContent = msg;
    skelSub.textContent = sub;
    skelSub.classList.remove('hidden');
    retryBtn.classList.remove('hidden');
  }

  function showSugar() {
    sugar.hidden = false;
    sugar.style.opacity = sugarReady ? '1' : '0';
    if (mv) { mv.hidden = true; mv.style.opacity = '0'; }
    activeViewer = 'sugar';
  }

  function wireSugarEvents(el) {
    el.addEventListener('init-model-load', () => {
      sugarReady = true;
      canActivateAR = true;
      clearTimeout(sugarFailTimer);
      if (activeViewer === 'sugar') {
        el.style.opacity = '1';
        hideSkeleton();
      }
      const runEnhance = () => { try { enhanceSugarShadow(); } catch (_) {} };
      setTimeout(runEnhance, 200);
      setTimeout(runEnhance, 800);
      setTimeout(runEnhance, 1800);
      setTimeout(runEnhance, 3500);
    });
    el.addEventListener('ar-status-changed', () => { /* keep alive */ });
  }
  wireSugarEvents(sugar);
  setTimeout(() => { try { enhanceSugarShadow(); } catch (_) {} }, 500);

  function rebuildSugarWithProduct(pid, orbit, target) {
    const wrapper = $('sugar-wrapper');
    if (!wrapper) return;
    const oldEl = sugar;
    const attrs = {};
    if (oldEl) Array.from(oldEl.attributes).forEach(a => attrs[a.name] = a.value);
    attrs['id'] = 'hero-sugar';
    attrs['product-id'] = pid;
    if (orbit)  attrs['camera-orbit']  = orbit;
    if (target) attrs['camera-target'] = target;
    const esc = (s) => String(s).replace(/"/g, '&quot;');
    const attrString = Object.entries(attrs).map(([k, v]) => `${k}="${esc(v)}"`).join(' ');
    const styleString = (oldEl ? oldEl.style.cssText : '') +
      'width:100%;height:100%;opacity:0;transition:opacity 500ms ease;background:transparent;';
    wrapper.innerHTML = `<sugar-model-viewer ${attrString} style="${esc(styleString)}"></sugar-model-viewer>`;
    sugar = wrapper.querySelector('sugar-model-viewer');
    wireSugarEvents(sugar);
  }

  function armSugarFailsafe() {
    clearTimeout(sugarFailTimer);
    sugarFailTimer = setTimeout(() => {
      if (sugarReady) return;
      showError('Connection trouble', 'Could not reach the model CDN. Check your network and tap retry.');
    }, 30000);
  }

  retryBtn?.addEventListener('click', () => {
    showSkeleton("reconnecting <span class=\"hero-render-msg__wink\" aria-hidden=\"true\">…</span>");
    const activeTab = document.querySelector('.tab.active');
    const pid = activeTab?.getAttribute('data-product-id') || sugar.getAttribute('product-id');
    const mobile = isMobileVP();
    const orbit  = mobile
      ? (activeTab?.getAttribute('data-sugar-orbit-mobile') || activeTab?.getAttribute('data-sugar-orbit'))
      : activeTab?.getAttribute('data-sugar-orbit');
    const target = mobile
      ? (activeTab?.getAttribute('data-sugar-target-mobile') || activeTab?.getAttribute('data-sugar-target'))
      : activeTab?.getAttribute('data-sugar-target');
    sugarReady = false;
    rebuildSugarWithProduct(pid, orbit, target);
    armSugarFailsafe();
    startGrooming();
  });

  tabs.forEach(tab => {
    tab.addEventListener('click', (e) => {
      e.preventDefault();
      if (tab.classList.contains('active')) return;
      tabs.forEach(t => {
        t.classList.remove('active');
        // WCAG 4.1.2: keep aria-selected in sync with visual state so
        // screen readers announce the new active tab correctly.
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      const viewerType = tab.getAttribute('data-viewer');
      const newTitle   = tab.getAttribute('data-mv-title');
      const newSpecs   = tab.getAttribute('data-mv-specs');
      title.textContent = newTitle;
      specs.textContent = newSpecs;
      updateViewProductLink();
      if (viewerType === 'sugar') {
        const pid    = tab.getAttribute('data-product-id');
        const mobile = isMobileVP();
        const orbit  = mobile
          ? (tab.getAttribute('data-sugar-orbit-mobile')  || tab.getAttribute('data-sugar-orbit'))
          : tab.getAttribute('data-sugar-orbit');
        const target = mobile
          ? (tab.getAttribute('data-sugar-target-mobile') || tab.getAttribute('data-sugar-target'))
          : tab.getAttribute('data-sugar-target');
        showSkeleton("wait it's rendering <span class=\"hero-render-msg__wink\" aria-hidden=\"true\">;)</span>", '');
        sugarReady = false;
        canActivateAR = false;
        showSugar();
        rebuildSugarWithProduct(pid, orbit, target);
        armSugarFailsafe();
        startGrooming();
      }
    });
  });

  function isMobileVP() { return window.innerWidth <= 900; }
  function applyOrbitFor(tab) {
    if (!tab) return;
    const mobile = isMobileVP();
    const orbit  = mobile
      ? (tab.getAttribute('data-sugar-orbit-mobile')  || tab.getAttribute('data-sugar-orbit'))
      : tab.getAttribute('data-sugar-orbit');
    const target = mobile
      ? (tab.getAttribute('data-sugar-target-mobile') || tab.getAttribute('data-sugar-target'))
      : tab.getAttribute('data-sugar-target');
    if (orbit)  sugar.setAttribute('camera-orbit', orbit);
    if (target) sugar.setAttribute('camera-target', target);
  }
  let resizeRAF;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeRAF);
    resizeRAF = requestAnimationFrame(() => applyOrbitFor(document.querySelector('.tab.active')));
  });
  applyOrbitFor(document.querySelector('.tab.active'));
  showSkeleton("wait it's rendering <span class=\"hero-render-msg__wink\" aria-hidden=\"true\">;)</span>", '');
  armSugarFailsafe();

  /* ---------- SUGAR SHADOW DOM ENHANCEMENT ---------- */
  function injectShadowCSS(root, id, css) {
    if (!root) return;
    try {
      let style = (root.querySelector && root.querySelector(`style[data-eid="${id}"]`)) || null;
      if (css) {
        if (!style && root.appendChild) {
          style = document.createElement('style');
          style.dataset.eid = id;
          root.appendChild(style);
        }
        if (style) style.textContent = css;
      } else if (style) {
        style.remove();
      }
      if (root.querySelectorAll) {
        root.querySelectorAll('*').forEach(child => {
          if (child.shadowRoot) injectShadowCSS(child.shadowRoot, id, css);
        });
      }
    } catch (e) {}
  }
  const SUGAR_HIDE_AR_CSS = `
    button.sugar_ar_button, button[part="default-ar-button"]:not([part="ar-prompt"]) {
      position: absolute !important; left: -10000px !important; top: -10000px !important;
      pointer-events: none !important; opacity: 0 !important;
    }`;
  const SUGAR_HIDE_FABRIC_CSS = `
    .fabricButtons, #fabricOpenButton, #fabricCloseButton,
    [id*="fabric" i], [class*="fabric-selector" i], [class*="fabricSelector" i],
    [class*="select-fabric" i], [class*="fabric-switcher" i], [class*="selectFabric" i],
    button[aria-label*="fabric" i], button[title*="fabric" i],
    .sugar-popup-show-button { display: none !important; visibility: hidden !important; }`;
  function enhanceSugarShadow() {
    if (!sugar) return;
    const root = sugar.shadowRoot || sugar;
    injectShadowCSS(root, 'ebarza-hide-ar',     SUGAR_HIDE_AR_CSS);
    injectShadowCSS(root, 'ebarza-hide-fabric', SUGAR_HIDE_FABRIC_CSS);
    function deepHide(rootEl) {
      if (!rootEl) return;
      if (rootEl.querySelectorAll) {
        rootEl.querySelectorAll('button, a, div, span').forEach(el => {
          if (el.id === 'ar-btn' || el.id === 'download-btn' || el.id === 'view-product-link' ||
              el.classList?.contains('btn-ar') || el.classList?.contains('btn-download') ||
              el.classList?.contains('view-product') || el.classList?.contains('text-stack')) return;
          if (el.closest?.('.text-stack')) return;
          const txt = (el.textContent || '').trim().toLowerCase();
          const aria = (el.getAttribute?.('aria-label') || '').toLowerCase();
          const isLeafFabric = (txt === 'select fabric' || txt === 'choose fabric' || txt === 'select your fabric')
                             && el.children.length <= 2;
          if (isLeafFabric || aria.includes('fabric')) {
            el.style.display = 'none'; el.style.visibility = 'hidden';
          }
          const isLeafAR = (txt === 'view in your home' || txt === 'view in your space')
                         && el.children.length <= 2
                         && !el.classList?.contains('btn-ar');
          if (isLeafAR) {
            el.style.position = 'absolute';
            el.style.left = '-10000px'; el.style.top = '-10000px';
            el.style.pointerEvents = 'none'; el.style.opacity = '0';
          }
        });
        rootEl.querySelectorAll('*').forEach(child => {
          if (child.shadowRoot) deepHide(child.shadowRoot);
        });
      }
    }
    deepHide(root); deepHide(sugar); deepHide(document.body);
    let globalStyle = document.head.querySelector('style[data-eid="ebarza-sugar-global"]');
    if (!globalStyle) {
      globalStyle = document.createElement('style');
      globalStyle.dataset.eid = 'ebarza-sugar-global';
      document.head.appendChild(globalStyle);
    }
    globalStyle.textContent = SUGAR_HIDE_FABRIC_CSS + ' ' + SUGAR_HIDE_AR_CSS;
  }
  function findSugarARButton() {
    function find(root) {
      if (!root) return null;
      if (root.querySelectorAll) {
        for (const b of root.querySelectorAll('button')) {
          const txt = (b.textContent || '').trim().toLowerCase();
          const aria = (b.getAttribute('aria-label') || '').toLowerCase();
          if (txt === 'view in your home' || txt === 'view in your space' ||
              aria.includes('view in your') || aria.includes('activate ar') ||
              b.classList?.contains('sugar_ar_button')) return b;
        }
        for (const el of root.querySelectorAll('*')) {
          if (el.shadowRoot) {
            const found = find(el.shadowRoot);
            if (found) return found;
          }
        }
      }
      return null;
    }
    return find(sugar?.shadowRoot) || find(sugar) || find(document.body);
  }

  /* ---------- DIMENSION TOGGLE GROOMING ---------- */
  function findInAllRoots(rootEl, selectors) {
    if (!rootEl || !rootEl.querySelectorAll) return [];
    const found = [];
    for (const sel of selectors) {
      try { rootEl.querySelectorAll(sel).forEach(el => found.push(el)); } catch (_) {}
    }
    rootEl.querySelectorAll('*').forEach(child => {
      if (child.shadowRoot) found.push(...findInAllRoots(child.shadowRoot, selectors));
    });
    return found;
  }
  const DIM_SELECTORS = [
    '.annototaion_control_toggle', '.DESIGN__toggle',
    '.dimension_open_button', '.dimension_close_button', '#dimension_button',
    '.dimension_control_button', '[class*="dimension_"]',
    '[class*="annototaion"]', '[class*="annotation_control"]',
    '[role="switch"][aria-label*="dimension" i]',
    '[role="switch"][aria-label*="measure" i]'
  ];
  /* `var` (not let) — hoists as undefined instead of a temporal-dead-zone
     ReferenceError if any call path reaches startGrooming() before this
     line executes. A TDZ crash here killed the whole script on live
     (2026-05-23): custom Measure button never injected + Sugar's native
     black AR button never hidden → phantom button blocking the CTA. */
  var groomTimer = null;
  var groomStopTimer = null;
  function groomSugarUI() {
    if (!sugar) return;
    const isMobileLayout = window.innerWidth <= 900;
    const arEls = [
      ...findInAllRoots(sugar.shadowRoot || sugar, ['.sugar_ar_button', 'div.sugar_ar_button']),
      ...findInAllRoots(sugar, ['.sugar_ar_button', 'div.sugar_ar_button'])
    ];
    arEls.forEach(el => {
      el.style.setProperty('position', 'absolute', 'important');
      el.style.setProperty('left', '-10000px', 'important');
      el.style.setProperty('top', '-10000px', 'important');
      el.style.setProperty('opacity', '0', 'important');
      el.style.setProperty('pointer-events', 'none', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('width', '1px', 'important');
      el.style.setProperty('height', '1px', 'important');
    });
    const dimEls = [
      ...findInAllRoots(sugar.shadowRoot || sugar, DIM_SELECTORS),
      ...findInAllRoots(sugar, DIM_SELECTORS),
      ...findInAllRoots(document.body, DIM_SELECTORS)
    ];
    Array.from(new Set(dimEls)).forEach(el => {
      el.style.setProperty('position', 'fixed', 'important');
      el.style.setProperty('top', '-10000px', 'important');
      el.style.setProperty('left', '-10000px', 'important');
      el.style.setProperty('opacity', '0', 'important');
      el.style.setProperty('visibility', 'hidden', 'important');
      el.style.setProperty('pointer-events', 'auto', 'important');
    });
    function fireSugarDimToggle() {
      const currentDimEls = [
        ...findInAllRoots(sugar.shadowRoot || sugar, DIM_SELECTORS),
        ...findInAllRoots(sugar, DIM_SELECTORS),
        ...findInAllRoots(document.body, DIM_SELECTORS)
      ];
      const target = currentDimEls.find(Boolean);
      if (!target) return;
      const clickable = target.querySelector('.toggle') || target.querySelector('.wrap') || target;
      try { clickable.click(); } catch (_) {}
    }
    const measureSlot = $('hero-measure-bar');
    if (measureSlot && !measureSlot.querySelector('.custom-measure-btn')) {
      const closedSrc = cfg.measureClosedSrc || '';
      const openSrc   = cfg.measureOpenSrc   || '';
      measureSlot.innerHTML = `
        <button class="custom-measure-btn" type="button" data-state="closed" style="
          background: ${isMobileLayout ? '#f4f4ec' : 'transparent'};
          border: ${isMobileLayout ? '1px solid rgba(140,120,109,0.2)' : '0'};
          padding: ${isMobileLayout ? '6px 14px 6px 16px' : '0'};
          border-radius: 999px; cursor: pointer;
          display: inline-flex; align-items: center; gap: 12px;
          font-family: 'Archivo', sans-serif;
          font-size: ${isMobileLayout ? '10px' : '11px'};
          letter-spacing: 0.22em; text-transform: uppercase;
          color: #1c1c1c; font-weight: 700; line-height: 1;
          ${isMobileLayout ? 'box-shadow:0 8px 18px -6px rgba(0,0,0,0.22);' : ''}">
          <span>Measure</span>
          <img class="measure-icon" src="${closedSrc}" alt="Off"
               style="height: ${isMobileLayout ? '22px' : '26px'}; width: auto; display: block; object-fit: contain;" />
        </button>`;
      const btn = measureSlot.querySelector('.custom-measure-btn');
      const icon = btn.querySelector('.measure-icon');
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        fireSugarDimToggle();
        const next = btn.dataset.state === 'closed' ? 'open' : 'closed';
        btn.dataset.state = next;
        icon.src = next === 'open' ? openSrc : closedSrc;
        icon.alt = next === 'open' ? 'On' : 'Off';
      });
    }
    document.querySelectorAll('button, a, div, span').forEach(el => {
      if (el.id === 'ar-btn' || el.id === 'download-btn' || el.id === 'view-product-link' ||
          el.closest?.('.text-stack')) return;
      const txt = (el.textContent || '').trim().toLowerCase();
      if ((txt === 'select fabric' || txt === 'choose fabric' || txt === 'select your fabric')
          && el.children.length <= 2) el.style.display = 'none';
    });
  }
  function startGrooming() {
    clearInterval(groomTimer); clearTimeout(groomStopTimer);
    groomTimer = setInterval(groomSugarUI, 250);
    groomStopTimer = setTimeout(() => clearInterval(groomTimer), 30000);
  }
  startGrooming();

  /* ---------- VIEW PRODUCT LINK ---------- */
  const vpLink = $('view-product-link');
  function updateViewProductLink() {
    const activeTab = document.querySelector('.tab.active');
    const handle = activeTab?.getAttribute('data-product-id');
    if (vpLink && handle) vpLink.href = (cfg.productBase || 'https://www.ebarza.com/products/') + handle;
  }
  updateViewProductLink();

  /* ---------- AR FLOW ---------- */
  function triggerAR() {
    try { if (activeViewer === 'sugar' && typeof sugar.activateAR === 'function') { sugar.activateAR(); return true; } } catch (e) {}
    try { const native = findSugarARButton(); if (native) { native.click(); return true; } } catch (e) {}
    try { if (mv && typeof mv.activateAR === 'function') { mv.activateAR(); return true; } } catch (e) {}
    return false;
  }
  function showQRModal(handle) {
    const existing = $('ebarza-qr-modal');
    if (existing) existing.remove();
    const url = (cfg.productBase || 'https://www.ebarza.com/products/') + handle;
    const qrApi = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(url)}&size=300x300&bgcolor=ffffff&color=1c1c1c&margin=10`;
    const overlay = document.createElement('div');
    overlay.id = 'ebarza-qr-modal';
    overlay.classList.add('ebarza3d-allowed');
    overlay.style.cssText = `position:fixed;inset:0;z-index:9999;background:rgba(28,28,28,0.85);backdrop-filter:blur(8px);display:grid;place-items:center;padding:24px;`;
    overlay.innerHTML = `
      <div style="background:#f4f4ec;border:1px solid rgba(140,120,109,0.18);border-radius:24px;max-width:420px;width:100%;padding:36px;text-align:center;box-shadow:0 50px 100px -40px rgba(0,0,0,0.5);">
        <p style="font-family:'Archivo',sans-serif;font-size:11px;letter-spacing:0.3em;text-transform:uppercase;color:#8c786d;font-weight:700;margin:0 0 14px;">— View in Your Space</p>
        <h3 style="font-family:'Archivo',sans-serif;font-weight:900;font-size:26px;letter-spacing:-0.02em;text-transform:uppercase;color:#1c1c1c;line-height:1.1;margin:0 0 22px;">Scan with<br/>your phone</h3>
        <div style="background:#fff;padding:18px;border-radius:18px;display:inline-block;margin-bottom:22px;border:1px solid rgba(140,120,109,0.14);">
          <img src="${qrApi}" alt="Scan to view in AR" style="width:220px;height:220px;display:block;" />
        </div>
        <p style="font-family:'Inter',sans-serif;font-size:14px;color:rgba(28,28,28,0.7);line-height:1.6;margin:0 0 24px;">Open this scan on your phone and tap the AR button to preview the model in your room through your camera.</p>
        <button data-close style="background:#1c1c1c;color:#f4f4ec;font-family:'Archivo',sans-serif;font-weight:700;font-size:11px;letter-spacing:0.22em;text-transform:uppercase;padding:14px 26px;border-radius:999px;border:0;cursor:pointer;">Close</button>
      </div>`;
    overlay.querySelector('[data-close]').onclick = () => overlay.remove();
    overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
    document.body.appendChild(overlay);
  }
  arBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    const isMobileVp = window.innerWidth < 768;
    if (isMobileVp) {
      if (triggerAR()) return;
      const wait = setInterval(() => { if (triggerAR()) clearInterval(wait); }, 200);
      setTimeout(() => clearInterval(wait), 10000);
    } else {
      const activeTab = document.querySelector('.tab.active');
      const handle = activeTab?.getAttribute('data-product-id') || 'wogg-bedstead-180-cm';
      showQRModal(handle);
    }
  });

  /* ---------- DOWNLOAD FLOW ---------- */
  const downloadBtn = $('download-btn');
  downloadBtn?.addEventListener('click', () => {
    const activeTab = document.querySelector('.tab.active');
    const handle = activeTab?.getAttribute('data-product-id');
    if (!handle) return;
    const downloadUrl = (cfg.downloadBase || 'https://ebarza.io/products/') + handle + '/download';
    if (cfg.isLoggedIn) {
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } else {
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      const loginUrl  = `/account/login?return_url=${returnUrl}`;
      showAuthGate(handle, downloadUrl, loginUrl);
    }
  });
  function showAuthGate(handle, downloadUrl, loginUrl) {
    const existing = $('auth-gate');
    if (existing) existing.remove();
    const overlay = document.createElement('div');
    overlay.id = 'auth-gate';
    overlay.classList.add('ebarza3d-allowed');
    overlay.style.cssText = `position:fixed;inset:0;z-index:200;background:rgba(28,28,28,0.55);backdrop-filter:blur(6px);display:grid;place-items:center;padding:24px;`;
    overlay.innerHTML = `
      <div style="background:#fff;border:1px solid rgba(140,120,109,0.18);border-radius:20px;max-width:440px;width:100%;padding:32px;font-family:'Inter',sans-serif;box-shadow:0 40px 80px -40px rgba(28,28,28,0.5);">
        <p style="font-family:'Archivo';font-size:11px;letter-spacing:0.3em;text-transform:uppercase;color:#8c786d;font-weight:600;margin:0 0 14px;">— Sign in to download</p>
        <h3 style="font-family:'Archivo';font-weight:900;font-size:24px;letter-spacing:-0.02em;text-transform:uppercase;color:#1c1c1c;line-height:1.1;margin:0 0 14px;">Get the .glb file</h3>
        <p style="font-size:14px;color:rgba(28,28,28,0.7);line-height:1.6;margin:0 0 22px;">Downloads are reserved for designers &amp; trade customers. Sign in or create your free designer account to continue.</p>
        <div style="display:flex;gap:10px;">
          <a href="${loginUrl}" style="flex:1;background:#1c1c1c;color:#f4f4ec;font-family:'Archivo';font-weight:700;font-size:12px;letter-spacing:0.22em;text-transform:uppercase;padding:14px 16px;border-radius:999px;text-align:center;text-decoration:none;">Sign in</a>
          <a href="/account/register" style="background:#fff;color:#1c1c1c;font-family:'Archivo';font-weight:700;font-size:12px;letter-spacing:0.22em;text-transform:uppercase;padding:14px 16px;border-radius:999px;border:1px solid rgba(140,120,109,0.22);text-align:center;text-decoration:none;">Register</a>
        </div>
        <button data-close style="margin-top:14px;background:transparent;border:0;font-family:'Archivo';font-size:11px;letter-spacing:0.22em;text-transform:uppercase;color:rgba(28,28,28,0.55);cursor:pointer;">Close</button>
      </div>`;
    document.body.appendChild(overlay);
    overlay.querySelector('[data-close]').onclick = () => overlay.remove();
    overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  }
})();
